import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pushEvent } from '@koob/google-tag-manager';
import { GTM_EVENTS } from '@koob/google-tag-manager/src/events';
import { useSetLoading } from '@koob/trip-designer';
import { getFolder } from 'api/folders';
import { createDefaultClientFolder } from 'utils/customerFile';
import { useKoobStore } from 'store';
import { saveTripDays, saveTrip } from '../../../../api/trip';
import {
  useApp,
  useBooking,
  useExperienceBooking,
  useSnack,
} from '../../../../hooks';
import { buildBasket } from './composeMapBasket';
import { getTotalTripPrice } from './composeMapTrip';
import {
  toGlobalOptionsPayload,
  toTravelersPayload,
  toTripDaysPayload,
  toTripPayload,
} from './composeMapSavePayload';

// The TO-only side of the compose screen: the client folder the trip hangs on,
// saving the trip as a draft or a template, and turning it into a basket. The
// four flags are what the footer and the guests modal read back.
export default function useComposeMapSave({
  tripId,
  trip,
  tripFilters,
  days,
  experiencesMap,
  hotelsMap,
  reloadTrip,
}) {
  const { t } = useTranslation('tripDesigner');
  const navigate = useNavigate();
  const { notify } = useSnack();
  const setLoading = useSetLoading();
  const { setFilters } = useBooking();
  const { setExperienceFilters, addExperienceToBasket } =
    useExperienceBooking();
  const refreshFolder = useKoobStore(state => state.refreshFolder);
  const { user, currentOrganizationIdInt } = useApp();

  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const createClientFolder = async () => {
    const newFolder = await createDefaultClientFolder(
      currentOrganizationIdInt,
      user,
    );
    tripFilters.clientFolder = newFolder;

    return newFolder;
  };

  const updateFolder = async () => {
    if (!tripFilters?.clientFolder?.id) {
      return null;
    }

    try {
      const newFolderData = await getFolder({
        id: tripFilters?.clientFolder?.id,
      });
      const newFolder = newFolderData.data;
      tripFilters.clientFolder = newFolder;

      return newFolder;
    } catch {
      // A trip distributed by a DMC carries that DMC's client folder, and
      // `GET /folder/:id` only serves the folders of the caller's organization:
      // the TO gets a 404. Refreshing the folder is decoration — the save must
      // go on with the folder the trip already holds, which the API re-attaches
      // unchanged.
      return null;
    }
  };

  const handleSave = async kind => {
    const travelers = toTravelersPayload(tripFilters);

    if (kind === 'draft') {
      if (!tripFilters?.clientFolder) {
        await createClientFolder();
      }
      await updateFolder();
    }

    setSaving(true);

    // Save trip with updated values first (date, duration, composition)
    await saveTrip(toTripPayload(trip, tripFilters));

    saveTripDays({
      tripId: tripId,
      tripDays: toTripDaysPayload(days),
      draftPrice: getTotalTripPrice(days, experiencesMap),
      folderId: kind === 'draft' ? tripFilters?.clientFolder?.id : undefined,
      travelers: travelers,
      globalOptions: toGlobalOptionsPayload(days),
    })
      .then(() => {
        notify(t('compose.savedAsDraft'));
        reloadTrip();
        // Keep the sidebar active-folder card in sync after the draft is
        // attached to the folder (US-016). No-op when no active folder.
        refreshFolder();

        pushEvent(GTM_EVENTS.TRIP_SAVED_DRAFT, {
          trip_id: tripId,
          days_count: days.length,
        });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleFinalize = async () => {
    if (finalizing) {
      return;
    }

    setFinalizing(true);

    let folder = tripFilters?.clientFolder;

    if (!folder) {
      await createClientFolder().then(f => (folder = f));
    }

    if (!folder?.id) {
      setFinalizing(false);
      return;
    }

    setLoading(true);

    await handleSave('draft');
    const basketContext = {
      days,
      experiencesMap,
      hotelsMap,
      tripFilters,
      tripId,
      setExperienceFilters,
      setFilters,
      addExperienceToBasket,
    };
    const promises = [];

    for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
      promises.push(buildBasket(basketContext, dayIndex));
    }

    await Promise.allSettled(promises);

    setSaving(false);
    setFinalizing(false);
    setLoading(false);

    pushEvent(GTM_EVENTS.BOOKING_STARTED, {
      trip_id: tripId,
      days_count: days.length,
    });

    navigate('/experiences/checkout');
  };

  return {
    saving,
    finalizing,
    handleSave,
    handleFinalize,
  };
}
